import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import { Button, buttonClasses, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { isEmpty, omit } from 'lodash';
import React from 'react';
import { BooleanParam, NumberParam, useQueryParams } from 'use-query-params';
import { ProcedureSlideBulkChangesSummary } from './DataGrids/ChangeSummaries';
import { DatasetMode, getDatasetMode, getDatasetModeQueryParams } from './datasetMode';
import { MultiplexSelectionSummary } from './MultiplexSelectionSummary';

interface DatasetModeSelectorProps {
  showNeedsAssignment?: boolean;
  needsAssignmentCount?: number;
}

export const DatasetModeSelector = ({
  showNeedsAssignment = false,
  needsAssignmentCount,
}: DatasetModeSelectorProps) => {
  const confirmWithModal = useConfirmation();
  const { bulkChanges, fieldsContext, hasChanges, clearChanges, setBulkEditMode } = useTableEditingContext<
    Procedure,
    ProceduresFieldsContext
  >();
  const { clearSelection } = useRowSelectionContext();
  const [{ slidesMode, pendingSlidesMode }, setDatasetQueryParams] = useQueryParams({
    slidesMode: BooleanParam,
    pendingSlidesMode: BooleanParam,
    page: NumberParam,
  });
  const activeMode = getDatasetMode({ slidesMode, pendingSlidesMode });

  const changeDatasetMode = async (nextMode: DatasetMode | null) => {
    if (!nextMode || nextMode === activeMode) {
      return;
    }

    const relevantChanges = {
      ...omit(bulkChanges, 'slide'),
      ...(slidesMode ? bulkChanges.slides || {} : {}),
    };
    const canChangeMode =
      !hasChanges ||
      isEmpty(relevantChanges) ||
      (await confirmWithModal({
        title: 'Undo Bulk Edits',
        text: (
          <Box>
            <p>You have unsaved changes. Are you sure you want to undo them?</p>
            <ProcedureSlideBulkChangesSummary bulkChanges={relevantChanges} fieldsContext={fieldsContext} />
          </Box>
        ),
      }));

    if (!canChangeMode) {
      return;
    }

    clearChanges();
    setBulkEditMode(false);
    clearSelection();
    setDatasetQueryParams(getDatasetModeQueryParams(nextMode), 'replaceIn');
  };

  const isNeedsAssignmentActive = activeMode === 'needsAssignment';

  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <Stack direction="row" alignItems="center" sx={{ paddingInline: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={activeMode}
          onChange={(_, nextMode: DatasetMode | null) => changeDatasetMode(nextMode)}
          aria-label="Dataset mode"
        >
          <ToggleButton value="cases" data-cy="case-mode-button">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <FolderOutlinedIcon fontSize="small" />
              <span>Cases</span>
            </Stack>
          </ToggleButton>
          <ToggleButton value="slides" data-cy="slides-mode-button">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PhotoLibraryOutlinedIcon fontSize="small" />
              <span>Slides</span>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
        {showNeedsAssignment ? (
          <Tooltip title="Slides in this study that still need a case assignment" describeChild>
            <Button
              variant="text"
              size="small"
              color="inherit"
              aria-pressed={isNeedsAssignmentActive}
              data-cy="needs-assignment-mode-button"
              startIcon={<RestorePageIcon fontSize="small" />}
              onClick={() => changeDatasetMode('needsAssignment')}
              sx={{
                ml: 1,
                px: 1,
                color: isNeedsAssignmentActive ? 'primary.main' : 'text.secondary',
                bgcolor: isNeedsAssignmentActive ? 'action.selected' : undefined,
                fontWeight: 500,
                textTransform: 'none',
                [`.${buttonClasses.startIcon}`]: { mr: 0.5 },
              }}
            >
              {needsAssignmentCount === undefined
                ? 'Slides awaiting assignment'
                : `Slides awaiting assignment · ${needsAssignmentCount}`}
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
      <Box sx={{ px: 1 }}>
        <MultiplexSelectionSummary />
      </Box>
    </Stack>
  );
};
